// @ts-nocheck — vendored/mirrored file: logic is byte-faithful to popin-rn and
// verified in its home repo's CI. The portal's tsconfig is stricter
// (noUncheckedIndexedAccess) than upstream's; the engine boundary is type-checked
// via ./derive.ts.
// ═══════════════════════════════════════════════════════════════════════════
// MIRRORED FROM popin-rn — DO NOT EDIT BY HAND.
//   source: smiletime/popin-rn  design/ds-sync/engine/treatments.ts
//   ref:    65f24d55aa9d4f5e978ff6697da875767f6cb8b2  (origin/main, fetched 2026-07-03)
// Byte-for-byte copy of the pop.in Theme Studio TREATMENT registry (PURE — no RN).
// Registry IS data: when popin-rn adds/edits a treatment this file goes stale.
// Drift is tracked by the source git SHA in ./provenance.ts (MIRRORED_FROM_SHA);
// re-sync via scripts/resync-theme-engine.sh. See ./README.md.
// ═══════════════════════════════════════════════════════════════════════════
/**
 * treatments — the TREATMENT registry (partner-portal "TREATMENT is a first-class
 * dimension"). A pop.in "mode" is THREE independent knobs:
 *
 *   PALETTE   — the brand seed → the anchor-and-generate engine (anchor-generate.ts)
 *   POLARITY  — dark or daylight ground
 *   TREATMENT — the display *material/character* — THIS FILE
 *
 * TREATMENT is an OPEN, NAMED registry (NOT a light/dark boolean). A treatment has
 * a KIND that decides how the palette and signature compose:
 *
 *   • EMISSIVE (NIGHT) — the device emits light. The engine generates the full
 *     brand palette (dark, brand-tinted); the treatment adds the signature overlay
 *     (lit-from-within glow, CRT scanline) and neon lit-segments. Light comes OUT.
 *
 *   • REFLECTIVE (DAYLIGHT / PAPER) — the device emits NOTHING; ambient light falls
 *     ON the surface and bounces back (reflective LCD, transflective, e-paper). This
 *     is a hard physical constraint with design consequences (researched 2026-06-28,
 *     5 cited passes — see GALLERY-PARK-REPORT.md):
 *       – the SUBSTRATE is a property of the MATERIAL, not the brand: a warm paper
 *         ground + charcoal ink, NEVER #fff/#000, in a compressed ~10:1 tonal window.
 *       – NO glow/bloom/neon is physically possible — emphasis is an INK change
 *         (reverse-video, weight, deep spot-colour), never a LIGHT change.
 *       – the only legal highlight is a SPECULAR sheen on the glass + a recessed-
 *         under-bezel inner shadow; titles are ENGRAVED, not glowed.
 *       – semantic accents are deep EARTH inks at distinct lightness steps (A24-muted,
 *         not arcade neon), used sparingly (colour-as-event).
 *       – the BRAND drives only the ACTION accent (its deep, desaturated sibling,
 *         derived through the engine's OKLCH) + an optional whisper — A24 restraint.
 *
 * THE INVARIANT (validity rule): across every palette AND treatment the four lit
 * segments (green=social · amber=currency · red=scarcity · brand=action) + the
 * dot-matrix device character + Jersey25 must read. EMISSIVE renders them as neon +
 * halo; REFLECTIVE as deep ink / reverse-video. Same meaning, different material.
 *
 * PURE DATA — no react-native imports (engine folder stays Node-runnable for the
 * report). `components/play/TreatmentStage.tsx` renders the material.
 */
import { buildEngineTheme, hexToOklch, oklchToHex, wcagContrast, type Appearance } from "./anchor-generate";
import { emissiveSignals, reflectiveSignals, type ReflectiveSubstrate } from "./signals";

export type TreatmentKind = "emissive" | "reflective";

/** #325 B3 — the OG / default polarity NAME. "NIGHT" is the canonical emissive
 *  default (the pop.in look every treatment riffs off), and is DISTINCT from the
 *  sub-theme SEEDS (dark_purple, dark_cyan, …) which name a HUE, not a polarity.
 *  Use this instead of hardcoding "dark"/"dark_purple" when you mean "the OG". */
export const DEFAULT_POLARITY = "NIGHT" as const;

// ─────────────────────────────────────────────────────────────────────────
// The ~50 "constant" semantic signals (social green · currency amber · scarcity
// red · feedback · presence · matchmaking · glitch · scrim · neutral chrome) are
// no longer hand-listed here. They live in the SIGNAL MANIFEST (signals.ts) and
// are RE-EXPRESSED per treatment complete-by-construction: NIGHT → shipped neon
// verbatim; reflective → deep earth inks against the substrate. This is the #322
// leak fix — one canonical list, every treatment emits every token.
// ─────────────────────────────────────────────────────────────────────────
// Reflective SUBSTRATE — the paper + ink of a material (brand-independent).
// ─────────────────────────────────────────────────────────────────────────
interface Substrate {
	background: string; // surfaceBackground — the "paper"
	muted: string; // surfaceMuted
	card: string; // surfaceCard
	elevated: string; // surfaceElevated (the dialog card lifts)
	ink: string; // textDefault — the charcoal
	inkSecondary: string;
	inkMuted: string;
	border: string; // borderDefault
	borderSubtle: string;
}

// ─────────────────────────────────────────────────────────────────────────
// Material — rendered by TreatmentStage into the signature layer.
// ─────────────────────────────────────────────────────────────────────────
export type TextureKind = "scanline" | "dither" | "none";
export type RecessKind = "full" | "soft" | "none";

export interface TreatmentMaterial {
	canvas: string[]; // the "room" ground (LinearGradient stops)
	captionInk: string;
	captionMuted: string;
	bezel: string[]; // the device bezel gradient
	bezelShadow: { color: string; opacity: number; radius: number; offsetY: number };
	screenRim: string;
	screenRimWidth: number;
	texture: TextureKind; // scanline (emissive) | dither tooth (reflective) | none
	sheen: boolean; // specular glass highlight (glossy reflective only)
	recess: RecessKind; // recessed-under-bezel inner shadow (reflective)
	grain: "none" | "fine" | "paper"; // authored paper/dither grain density
	/**
	 * The charming "crank" — an engraved caption on the bezel chin, an A24-restrained
	 * homage to the Game-Boy "DOT MATRIX WITH STEREO SOUND" printed device line. A real
	 * chassis detail (printed into the plastic), so it stays material-honest. `led` is
	 * the tiny power-indicator lens (a real emissive chassis component, off the screen).
	 */
	deviceCaption?: { mark: string; line: string; ink: string; led?: string };
}

export interface Treatment {
	id: string;
	name: string;
	spec: string;
	polarity: Appearance;
	kind: TreatmentKind;
	emissive: boolean;
	material: TreatmentMaterial;
	substrate?: Substrate; // present iff reflective
}

export const TREATMENTS: Record<string, Treatment> = {
	// ── EMISSIVE ────────────────────────────────────────────────────────────
	NIGHT: {
		id: "NIGHT",
		name: "Night",
		spec: "Emissive · self-lit · OLED / CRT",
		polarity: "dark",
		kind: "emissive",
		emissive: true,
		material: {
			canvas: ["#1d1530", "#0b0911", "#070609"],
			captionInk: "#f1ebfb",
			captionMuted: "#9a8fb8",
			bezel: ["#221c2c", "#15111c"],
			bezelShadow: { color: "#000000", opacity: 0.7, radius: 34, offsetY: 26 },
			screenRim: "rgba(255,255,255,0.08)",
			screenRimWidth: 1,
			texture: "scanline",
			sheen: false,
			recess: "none",
			grain: "none",
		},
	},

	// ── REFLECTIVE FAMILY ───────────────────────────────────────────────────
	// 1) THE HERO — warm cream reflective LCD (Playdate × A24).
	"DAYLIGHT-LCD": {
		id: "DAYLIGHT-LCD",
		name: "Daylight · Reflective LCD",
		spec: "Reflective · ambient-lit · warm cream panel",
		polarity: "light",
		kind: "reflective",
		emissive: false,
		substrate: {
			background: "#E9E3D2", // warm cream paper
			muted: "#E3DCC8",
			card: "#DED6C0",
			elevated: "#EFE9D9", // the card lifts (lighter, sky-lit)
			ink: "#2A251C", // warm charcoal
			inkSecondary: "#5C5446",
			inkMuted: "#8C8474",
			border: "rgba(42,37,28,0.22)",
			borderSubtle: "rgba(42,37,28,0.13)",
		},
		material: {
			canvas: ["#cfc7b4", "#bcb39d"],
			captionInk: "#2A251C",
			captionMuted: "#6b6253",
			bezel: ["#efe9da", "#d8cfb9"],
			bezelShadow: { color: "#6b614a", opacity: 0.5, radius: 30, offsetY: 22 },
			screenRim: "rgba(120,108,84,0.45)",
			screenRimWidth: 1.5,
			texture: "dither",
			sheen: true,
			recess: "full",
			grain: "fine",
			deviceCaption: { mark: "pop.in", line: "DOT MATRIX · LIVE PRESENCE", ink: "#6b6253", led: "#b0473a" },
		},
	},

	// 2) TRANSFLECTIVE — cool green-gray (Game Boy DMG reflective-film lineage).
	"DAYLIGHT-GREEN": {
		id: "DAYLIGHT-GREEN",
		name: "Daylight · Transflective",
		spec: "Reflective · ambient-lit · green-gray panel",
		polarity: "light",
		kind: "reflective",
		emissive: false,
		substrate: {
			background: "#CACFB8", // pea-green reflective ground (lightened for segment headroom)
			muted: "#C3C9B0",
			card: "#BCC2A8",
			elevated: "#D1D6C1",
			ink: "#262C20", // dark olive ink
			inkSecondary: "#474D3B", // darkened so secondary text clears AA on the light green ground
			inkMuted: "#727866",
			border: "rgba(38,44,32,0.26)",
			borderSubtle: "rgba(38,44,32,0.14)",
		},
		material: {
			canvas: ["#b3b89f", "#9ca383"],
			captionInk: "#262C20",
			captionMuted: "#5c6149",
			bezel: ["#dcdcc9", "#c2c3a9"],
			bezelShadow: { color: "#5c5a3f", opacity: 0.5, radius: 30, offsetY: 22 },
			screenRim: "rgba(70,76,54,0.5)",
			screenRimWidth: 1.5,
			texture: "dither",
			sheen: true,
			recess: "full",
			grain: "fine",
			deviceCaption: { mark: "pop.in", line: "DOT MATRIX · STEREO PRESENCE", ink: "#566049", led: "#9c5a3f" },
		},
	},

	// 3) E-PAPER — warm matte print (calm reading mode; NO sheen — matte = no specular).
	"PAPER-EINK": {
		id: "PAPER-EINK",
		name: "Paper · E-ink",
		spec: "Reflective · matte / printed · no glare",
		polarity: "light",
		kind: "reflective",
		emissive: false,
		substrate: {
			background: "#E8E2D4", // warm e-paper
			muted: "#E2DBCA",
			card: "#DCD4C1",
			elevated: "#ECE6D8",
			ink: "#211E16", // deepest ink (e-paper has the best blacks here)
			inkSecondary: "#564E40",
			inkMuted: "#8A8273",
			border: "rgba(33,30,22,0.18)",
			borderSubtle: "rgba(33,30,22,0.10)",
		},
		material: {
			canvas: ["#ddd6c6", "#cbc3b0"],
			captionInk: "#211E16",
			captionMuted: "#6f654d",
			bezel: ["#f3eee0", "#e0d9c7"],
			bezelShadow: { color: "#8a7f63", opacity: 0.42, radius: 26, offsetY: 18 },
			screenRim: "rgba(33,30,22,0.16)",
			screenRimWidth: 1,
			texture: "none",
			sheen: false, // matte — e-paper has no specular glare
			recess: "soft",
			grain: "paper", // heavier authored paper grain
			deviceCaption: { mark: "pop.in", line: "E-PAPER · QUIET HOURS", ink: "#6f654d", led: "#9a6a4a" },
		},
	},
};

// ─────────────────────────────────────────────────────────────────────────
// Brand → deep reflective ACTION accent. Take the seed into OKLCH, pull chroma
// down and lightness to a deep ~0.42, regamut, then darken until a cream label
// clears AA 4.5. This is the brand's "ink sibling" — the one spot colour the
// reflective treatment lets the brand drive (A24 colour-as-event).
// ─────────────────────────────────────────────────────────────────────────
const REFLECTIVE_ON_ACTION = "#F4F0E2";
function deepInkFromSeed(seed: string): { action: string; onAction: string } {
	const o = hexToOklch(seed);
	const C = Math.min(o.C * 0.66, 0.13);
	let L = 0.42;
	let hex = oklchToHex({ L, C, H: o.H });
	for (let i = 0; i < 40 && wcagContrast(REFLECTIVE_ON_ACTION, hex) < 4.6; i++) {
		L -= 0.015;
		hex = oklchToHex({ L, C, H: o.H });
	}
	return { action: hex, onAction: REFLECTIVE_ON_ACTION };
}

const withAlpha = (hex: string, a: number): string => {
	const h = hex.replace("#", "");
	const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
	return `rgba(${r}, ${g}, ${b}, ${a})`;
};

/**
 * Raw 12-step color ramp for a reflective (light) substrate. buildEngineTheme
 * emits color1..12 for the EMISSIVE path; the reflective path needs its own so
 * direct `$color1..$color12` reads — the Play title (`$color9` via
 * useTabThemeColors), the sheet handle (`$color8`), dividers (`$color4`) —
 * repaint to paper/ink instead of inheriting the dark theme's purple ramp.
 * Mirrors buildEngineTheme(light): color1 lightest paper → color12 darkest ink,
 * the deep-brand action injected at 9/10. Mid steps walk card→ink in OKLCH
 * lightness at a low paper chroma so greys stay paper-warm, never cool.
 */
function reflectiveRamp(s: Substrate, action: string): Record<string, string> {
	const card = hexToOklch(s.card);
	const ink = hexToOklch(s.ink);
	const step = (t: number) =>
		oklchToHex({ L: card.L + (ink.L - card.L) * t, C: Math.min(ink.C, 0.012), H: ink.H });
	const act = hexToOklch(action);
	return {
		color0: withAlpha(s.ink, 0),
		color1: s.background,
		color2: s.muted,
		color3: s.card,
		color4: step(0.16),
		color5: step(0.3),
		color6: step(0.46),
		color7: step(0.62),
		color8: step(0.78),
		color9: action,
		color10: oklchToHex({ L: Math.max(act.L - 0.05, 0.1), C: act.C, H: act.H }),
		color11: s.inkSecondary,
		color12: s.ink,
	};
}

/** Emit the full DS token override for a REFLECTIVE cell: material substrate +
 *  deep-brand action + deep-earth segments + every glow zeroed. Mirrors the key
 *  set buildEngineTheme emits, so it fully repaints whatever registered theme it
 *  is injected into (no dark bleed). */
function buildReflectiveTheme(seed: string, t: Treatment): Record<string, string> {
	const s = t.substrate!;
	const { action, onAction } = deepInkFromSeed(seed);
	return {
		// raw 12-step ramp (so direct $color1..$color12 reads repaint to paper/ink)
		...reflectiveRamp(s, action),
		// base Tamagui keys
		background: s.background,
		backgroundHover: s.muted,
		backgroundPress: s.card,
		backgroundFocus: s.muted,
		borderColor: s.border,
		borderColorHover: s.border,
		borderColorPress: s.borderSubtle,
		borderColorFocus: action,
		color: s.ink,
		colorHover: s.inkSecondary,
		colorPress: s.ink,
		colorFocus: s.inkSecondary,
		placeholderColor: s.inkMuted,
		accentBackground: action,
		accentColor: onAction,
		// semantic map (mirrors withSemanticTokens / buildEngineTheme)
		actionPrimary: action,
		actionPrimaryGlow: withAlpha(action, 0), // NO glow on reflective
		actionOnPrimary: onAction,
		actionSecondaryBorder: s.border,
		actionOnSecondary: s.inkSecondary,
		actionTertiary: s.muted,
		actionOnTertiary: s.ink,
		stateScheduled: s.inkMuted,
		stateOnScheduled: s.ink,
		stateEnded: s.border,
		stateOnEnded: s.inkSecondary,
		stateUnread: withAlpha(action, 0.12),
		stateRead: s.muted,
		surfaceBackground: s.background,
		surfaceMuted: s.muted,
		surfaceCard: s.card,
		surfaceElevated: s.elevated,
		textDefault: s.ink,
		textSecondary: s.inkSecondary,
		textMuted: s.inkMuted,
		textDisabled: s.borderSubtle,
		borderDefault: s.border,
		borderFocus: action,
		borderSubtle: s.borderSubtle,
		headerBackground: s.background,
		brandAccent: action,
		brandAccentGlow: withAlpha(action, 0), // NO glow
		playAccent: s.inkSecondary, // #325 B3 — PLAY standout (color11 equiv on paper)
		// ── the ~50 constant signals, re-expressed as deep earth inks against
		// this substrate (complete-by-construction — the #322 leak fix) ──
		...reflectiveSignals(s as ReflectiveSubstrate),
	};
}

/**
 * buildCellTheme — the full `updateTheme` payload for one treatment cell. Emits
 * the COMPLETE token contract (ramp + semantic map + every constant signal) so
 * NO token falls back to a NIGHT value on a non-NIGHT treatment (#322 leak fix).
 *   EMISSIVE   → engine ramp + neon signals verbatim (NIGHT is the reference).
 *                referenceOnly → signals only, so pop.in-NIGHT keeps the SHIPPED
 *                dark_purple ramp untouched (its ~46 ramp/semantic keys already
 *                live on the base theme; the signals are no-op night values).
 *   REFLECTIVE → material substrate + deep-brand action + re-expressed deep-earth
 *                signals (buildReflectiveTheme already folds in reflectiveSignals).
 * Composition is always PALETTE(seed) × POLARITY × TREATMENT.
 */
export function buildCellTheme(
	seed: string,
	treatment: Treatment,
	referenceOnly = false,
): Record<string, string> {
	if (treatment.kind === "reflective") return buildReflectiveTheme(seed, treatment);
	if (referenceOnly) return { ...emissiveSignals() };
	return { ...buildEngineTheme(seed, treatment.polarity), ...emissiveSignals() };
}
